'use client';

import { useEffect, useState } from 'react';
import { type Product } from '@/lib/productService';
import { ProductCard } from '@/components/ProductCard';
import { useTranslations } from 'next-intl';

type Props = {
  productIds: string[];
  storeId: string;
  locale: string;
};

export function CartRelatedProducts({ productIds, storeId, locale }: Props) {
  const t = useTranslations('product');
  const [products, setProducts] = useState<Product[]>([]);
  const [publicStoreId, setPublicStoreId] = useState(storeId);

  const productIdsKey = productIds.join(',');

  useEffect(() => {
    if (productIds.length === 0) {
      setProducts([]);
      return;
    }

    fetch(`/api/stores/${storeId}/related-products?productIds=${productIdsKey}&limit=8`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { products: Product[]; publicStoreId: string } | null) => {
        if (data?.products?.length) {
          setProducts(data.products);
          setPublicStoreId(data.publicStoreId ?? storeId);
        } else {
          setProducts([]);
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdsKey, storeId]);

  if (products.length === 0) return null;

  return (
    <section className="mt-12">
      <h2 className="text-base font-medium leading-snug mb-6">{t('relatedProducts')}</h2>
      <div className="relative -mx-4 px-4 md:mx-0 md:px-0">
        <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="flex gap-4 md:gap-6">
            {products.map((product) => (
              <div key={product.id} className="flex-shrink-0 w-[calc(50%-0.5rem)] md:w-[280px]">
                <ProductCard
                  product={product}
                  storeId={storeId}
                  publicStoreId={publicStoreId}
                  locale={locale}
                  isStoreClosed={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
